import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import { TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import constants from '../../../constants';
import MoveTypeDropDown from "../../../uiControls/MoveTypeDropDown";
import {Search} from "../../../filtering";
import {NavigationType, openNewPage} from '../../../util/navigationHelpers';
import {
  DateRenderer,DateTimeRenderer,NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,TextRenderer
} from '../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../uiControls/UniqueLinkButton';
import PrimaryButton from '../../../ui/PrimaryButton';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../common/JobListViewContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import {JobNames} from "../../../framework.core/JobNames";
import { oeQuotationList } from '../tableSettings';
import {OeQuoteListItem} from "../../../fms.data/models/OeQuoteListItem";
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import {QuotationArgs} from '../../../fms.report/args';
import {DocumentType} from '../../../framework.core/DocumentType';
import useReportViewer from '../../../hooks/useReportViewer';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../ui/menu/Menu';
import * as XCol from '../../../framework.core/XExportColumn';
import { FmsSecTask } from '../../../fms.core/constants';
import { findSecurityTask } from '../../../util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { DeleteIconButton, DeleteIconButton2, EditIconButton, EditIconButton2, PrintIconButton, ViewIconButton, ViewIconButton2 } from '../../../ui/IconButton';


function MoveTypeFilterRenderer({value, onChange}: any) {
  return (
    <MoveTypeDropDown
    name='ifms-filter-moveType-select'
      value={value}
      onChange={moveType => onChange(moveType)}
    />
  );
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();
  const sourceUrl = `${history.location.pathname}${history.location.search}`;
  const operation = sourceUrl.includes('oceanImport') ? 'oceanImport' : 'oceanExport';

  const selectedElement = jobListContext.selectedElement as OeQuoteListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const { viewReport } = useReportViewer();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/quotation/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress.</p>);
      return;
    }

    openNewPage(history, `/${operation}/quotation/add`, NavigationType.newPage);
  }

  async function deleteQuotation() {
    if (!selectedElement) {
      return;
    }

    if (
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/quotation/edit/${selectedElement.id}`) || 
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/quotation/view/${selectedElement.id}`)
    ) {
      await showGlobalOkDialog('Need to close Quotation first', false, null, "warning");
      return;
    }

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    ,false, 0, true, 'warning');

    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.OEQuotation, { id:selectedElement.id, pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message, false, null, 'warning');
      }
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }
    const id = selectedElement.id;
    const editUrl = `/${operation}/quotation/edit/${id}`;
    const viewUrl = `/${operation}/quotation/view/${id}`;

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/${operation}/quotation/edit/${id}`;
    const viewUrl = `/${operation}/quotation/view/${id}`;
    const listSec = findSecurityTask(appContext, (operation === 'oceanImport' ? FmsSecTask.OIQuot : FmsSecTask.OEQuot));
    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }

    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/quotation/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress.</p>);
      return;
    }

    const id = selectedElement.id;
    openNewPage(history, `/${operation}/quotation/add?copyId=${id}`, NavigationType.newPage);
  }

  async function print() {
    const title = `OE Quotation ${selectedElement.quoteNo}`;
    const args = new QuotationArgs({
      docType: DocumentType.Quotation,
        formCaption: title,
        subject: title,

      quoteID: selectedElement.id,
      branch: appContext.user?.branch??'',
      toCompanyid: `${selectedElement.agentId}${String.fromCharCode(127)}${selectedElement.customerId}`,
      tbName: 'T_OOHQUOTATION',
      tbId: selectedElement.id?.toString()||'0',
    })
    viewReport(args, "", true);
  }

  return {create, deleteQuotation, edit, view, copy, print, operation};
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create, deleteQuotation, edit, view, copy,  operation } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
   const appContext = useContext(ApplicationContext);

  const listSec = findSecurityTask(appContext, (operation === 'oceanImport' ? FmsSecTask.OIQuot : FmsSecTask.OEQuot));

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, hidden: !listSec?.grantAdd },
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantEdit && !listSec?.grantDel },
    { label: 'Edit', shortcut: 'F3', separator: !listSec?.grantDel, hidden: !listSec?.grantEdit },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel },
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: constants.Doc_Server, shortcut: 'F8', separator: true, newCol: true },
    { label: 'Copy', shortcut: 'Ctrl+F2', separator: true, hidden: !listSec?.grantAdd },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="quotation-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        deleteQuotation();
        break;
      case 4:
        jobListContext.triggerTableSearch('F5');
        break;
      case 5:
        jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
        break;
      case 6:
        copy();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }} // xkdldj Eprh
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);

  const selectedElement = jobListContext.selectedElement as OeQuoteListItem;

  const {deleteQuotation, edit, view, copy, print, operation} = useSelectedOperations();

  function hasOpenedNew() {
    return jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/quotation/add`);
  }

  const listSec = findSecurityTask(appContext, (operation === 'oceanImport' ? FmsSecTask.OIQuot : FmsSecTask.OEQuot));
  
  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  const canBeAdd = !hasOpenedNew() && listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;
    const canBeCopied = hasSelectedRow && !hasOpenedNew() && listSec?.grantAdd;

  return (
    <div className='ifms-common-no-vertical-separator-gap-margin'>
     <div className='ifms-common-vertical-gap-8'>
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ?  <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}/> : <></>} */}
        <UniqueLinkButton newStyle name='quotation-list-add-button' display="primary" link={`/${operation}/quotation/add`} disabled={!canBeAdd} label={<FontAwesomeIcon icon={faPlus} />} useOnlyIcon data-tooltip='New' iconMode/>
        <EditIconButton2 name='quotation-list-edit-button' disabled={!canBeEdit} onClick={edit} data-tooltip='Edit' useText/>
        <ViewIconButton2 name='quotation-list-view-button' disabled={!hasSelectedRow} onClick={view} data-tooltip='View' useText />
        <DeleteIconButton2 name='quotation-list-delete-button' disabled={!canBeDeleted} onClick={deleteQuotation} data-tooltip='Delete' useText/>
      </div>
      <div className='ifms-common-vertical-gap-8'>
        <PrimaryButton name='quotation-list-copy-button' disabled={!canBeCopied} onClick={copy} size={'extrasmall'}>Copy/Paste</PrimaryButton>
        <PrintIconButton name='quotation-list-print-button' disabled={!hasSelectedRow} onClick={print} data-tooltip='Print Quotation' useText/>
      </div>
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);

  const {copy, create, edit, view, operation} = useSelectedOperations();
  const listSec = findSecurityTask(appContext, (operation === 'oceanImport' ? FmsSecTask.OIQuot : FmsSecTask.OEQuot));

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }
      doProcessFkey();
    },
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd)
        create();
    } else if (unprocessedFkey === "CTRL+F2") {
      if(listSec?.grantAdd)
        copy();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F2', 'CTRL+F2', 'F3', 'SHIFT+F4', 'F9', 'F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }


  return (
    <InfoAreaDrawer
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.OEQuotation}
      jobId={(jobListContext.selectedElement as OeQuoteListItem | null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
    />
  );
}

function AfterMemoLogButtonCommands() {
  return null;
}

/**
 * Concrete implementation for Quotation List screen
 *
 * @class QuotationListView
 * @extends {AbstractListView}
 */
class QuotationListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    // const searchParams = OeQuoteListItem.calculateSearchParameters(search, OeQuoteListItem.MapOeSearchFields);
    // searchParams['opType'] = opType === 'oceanImport' ? 'I' : 'E';

    // if (searchParams.searchField === undefined) {
    //   searchParams.searchField = ['opType']
    // } else {
    //   searchParams.searchField.push('opType');
    // }

    const apiResult = await api.getListItems(
      JobNames.OEQuotation,
      'OeQuoteListItem',
      OeQuoteListItem.calculateSearchParameters(search, OeQuoteListItem.MapOeSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: OeQuoteListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount}; 
  }


  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.OEQuotation, 'OeQuoteListItem',
      OeQuoteListItem.calculateSearchParameters(search, OeQuoteListItem.MapOeSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  OeQuoteListItem.formatData(apiResult.entities);
  }


  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'serviceType',
        renderer: MoveTypeFilterRenderer,
        searchValueExtractor: (v: any | null | undefined) => v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    // return [
    //   {accessor: 'status', Cell: IndicatorRenderer},
    //   {accessor: 'validBy', Cell: DateRenderer},
    //   {accessor: 'u2Date', Cell: DateTimeRenderer},
    //   {accessor: 'quoteDate', Cell: DateRenderer},
    //   {accessor: 'operator', Cell: TextAlignLeftRenderer},
    //   {accessor: 'quoteNo', Cell: TextAlignLeftRenderer},
    //   {accessor: 'sName', Cell: TextAlignLeftRenderer},
    //   {accessor: 'contactName', Cell: TextAlignLeftRenderer},
    //   {accessor: 'hBookingNo', Cell: TextAlignLeftRenderer},
    //   {accessor: 'serviceType', Cell: TextAlignLeftRenderer},
    //   {accessor: 'commodity', Cell: TextAlignLeftRenderer},
    //   {accessor: 'lPort', Cell: TextAlignLeftRenderer},
    //   {accessor: 'discharge', Cell: TextAlignLeftRenderer},
    //   {accessor: 'plofRec', Cell: TextAlignLeftRenderer},
    //   {accessor: 'finalDest', Cell: TextAlignLeftRenderer},
    //   {accessor: 'plofDel', Cell: TextAlignLeftRenderer},
    //   {accessor: 'sManID', Cell: TextAlignLeftRenderer},
    //   {accessor: 'u1ID', Cell: TextAlignLeftRenderer},
    //   {accessor: 'u2ID', Cell: TextAlignLeftRenderer},
    // ];
    return OeQuoteListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.dataIndex === "status") {
        colRenderer.Cell = () => <span></span>;
        colRenderer.Header = () => <span></span>;
      }
      // { accessor:'hblCount', Cell: HblRenderer, Header: () => <span><u>HBL</u>(s)</span> },
      else if(x.dataIndex === 'customer') {
        colRenderer.Cell = TextAlignLeftRenderer;
      }
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'createdAt',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  oeQuotationList;
    const cols = OeQuoteListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  } 

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: OeQuoteListItem) {
    return row && row.id ? `/oceanExport/quotation/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'OE Quotation List';
  };

  renderAdditionalCommands() {
    return null;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  commandsAfterFilterBtn():React.ReactNode{
    return <AfterMemoLogButtonCommands />
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
}

export default QuotationListView;
